import { InvestPaymentMethodType, InvestPaymentMethodTypes, PaymentPageType } from '../../../types/mf-execution.types'
import { investPaymentMethodConfig, upiIcon } from '../../../utils/mf-execution/paymentPageConfig'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { InvestPaymentMethodsBottomSheet } from '../../BottomSheetComponents/mf-execution/InvestPaymentMethodsBottomSheet'
import { PaymentMethodsButton } from '../PaymentMethodsButton'
import { useEffect, useMemo, useState } from 'react'
import { usePaymentMethodAutoSwitchLegacy } from './usePaymentMethodAutoSwitch.legacy'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import { BuyPaymentMethodsButtonLegacyProps } from './buyPaymentMethodsButton.types'

export const BuyPaymentMethodsButtonLegacy = ({
  amount,
  isDisabled,
  handlePayNowClick,
  selectedPaymentMethod,
  handlePaymentMethodChange,
  paymentMethods,
  handleAddUPIClick,
  paymentType,
  trackingFund,
}: BuyPaymentMethodsButtonLegacyProps) => {
  const [noUpiMethod, setNoUpiMethod] = useState(false)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const { blockBecauseNotAvailablePaymentMethod, setIsUserExplicitSelection } =
    usePaymentMethodAutoSwitchLegacy({
      amount,
      paymentMethods,
      selectedPaymentMethod,
      handlePaymentMethodChange,
    })

  useEffect(() => {
    if (paymentMethods && !selectedPaymentMethod) {
      const upiMethods = paymentMethods?.filter((method) => method?.type === InvestPaymentMethodTypes.UPI)
      if (upiMethods?.length === 0) {
        setNoUpiMethod(true)
        handlePaymentMethodChange(null)
      } else {
        setNoUpiMethod(false)
        handlePaymentMethodChange(upiMethods?.[0] ?? null)
      }
    }
  }, [])

  const selectedPaymentConfig = useMemo(() => {
    if (!selectedPaymentMethod) {
      return null
    }

    return investPaymentMethodConfig?.[selectedPaymentMethod?.type] ?? null
  }, [selectedPaymentMethod])

  const middleTexts = useMemo(
    () => [
      {
        type: 'WITH_DOWN_CARAT' as const,
        text:
          selectedPaymentMethod?.type === InvestPaymentMethodTypes.UPI
            ? selectedPaymentMethod?.upiId || selectedPaymentConfig?.label || ''
            : selectedPaymentConfig?.label || '',
      },
      {
        type: 'NORMAL' as const,
        text: selectedPaymentMethod?.displayAccNumber || '',
      },
    ],
    [selectedPaymentMethod, selectedPaymentConfig]
  )

  const sortPaymentMethods = (methods: InvestPaymentMethodType[]): InvestPaymentMethodType[] => {
    const LOWEST_PRIORITY = 999
    const priority: Record<InvestPaymentMethodTypes, number> = {
      [InvestPaymentMethodTypes.MANDATE]: 1,
      [InvestPaymentMethodTypes.UPI]: 2,
      [InvestPaymentMethodTypes.NET_BANKING]: 3,
      [InvestPaymentMethodTypes.UPI_INTENT]: 4,
    }
    return [...methods]?.sort((a, b) => {
      const aType = (a?.type as InvestPaymentMethodTypes) ?? ''
      const bType = (b?.type as InvestPaymentMethodTypes) ?? ''
      const priorityA = priority[aType] ?? LOWEST_PRIORITY
      const priorityB = priority[bType] ?? LOWEST_PRIORITY

      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }

      return aType?.localeCompare(bType) ?? 0
    })
  }

  const handlePaymentMethodsClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: paymentType === PaymentPageType.SIP ? 'SIP payment' : 'One time Enter amount',
        eventId: 'MFE_SELECT_AMOUNT_CTA_SELECT_BANK',
        buttonLabel: 'Payment method',
        ...(trackingFund ?? {}),
      },
    })
    openBottomSheet(
      <InvestPaymentMethodsBottomSheet
        onDismiss={closeBottomSheet}
        ScreenName='MFExecution'
        paymentMethods={sortPaymentMethods(
          paymentMethods?.filter((method) => method?.type !== InvestPaymentMethodTypes.MANDATE) ?? []
        )}
        selectedPaymentMethod={selectedPaymentMethod}
        handlePaymentMethodChange={(paymentMethod) => {
          setNoUpiMethod(false)
          setIsUserExplicitSelection(true)
          handlePaymentMethodChange(paymentMethod)
        }}
        amount={amount}
        isPaymentDisabled={isDisabled}
        trackingFund={trackingFund}
        onAddUpiClick={() => {
          closeBottomSheet()
          handleAddUPIClick()
        }}
      />
    )
  }

  if (noUpiMethod) {
    return (
      <PaymentMethodsButton
        iconSVG={upiIcon}
        onMiddleTextsClick={handlePaymentMethodsClick}
        middleTexts={[{ type: 'WITH_DOWN_CARAT', text: 'Pay with UPI' }]}
        onCTAClick={() => handlePayNowClick('ADD_UPI')}
        ctaLabel='Proceed'
        isDisabled={isDisabled}
      />
    )
  }

  if (!selectedPaymentConfig || !selectedPaymentMethod || !paymentMethods) {
    return null
  }

  return (
    <PaymentMethodsButton
      iconSVG={selectedPaymentConfig?.icon}
      onMiddleTextsClick={handlePaymentMethodsClick}
      middleTexts={middleTexts}
      onCTAClick={() => handlePayNowClick('PAY_NOW')}
      ctaLabel={paymentType === PaymentPageType.SIP ? 'Continue' : 'Pay now'}
      isDisabled={isDisabled || blockBecauseNotAvailablePaymentMethod}
    />
  )
}
